import React, { useMemo } from 'react';

/**
 * Animated SVG line-chart that visualises the risk/volatility profile of an
 * investment class. Draws itself in on scroll-reveal via stroke-dashoffset
 * and then breathes gently.
 *
 * variant:
 *   'trading' — jagged, many small spikes, mildly downward
 *   'stocks'  — smooth sinus with slight upward drift
 *   'crypto'  — extreme spikes, huge amplitude
 */
export default function VolatilityWave({ variant = 'trading', width = 320, height = 64 }) {
  const points = useMemo(() => generatePoints(variant, width, height), [variant, width, height]);
  const pathD = useMemo(() => toPath(points), [points]);
  const totalLen = Math.max(600, points.length * 12);

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      width={width}
      height={height}
      className={`vol-wave vol-wave--${variant}`}
      aria-hidden="true"
      preserveAspectRatio="none"
    >
      {/* baseline hairline */}
      <line
        x1={0}
        x2={width}
        y1={height / 2}
        y2={height / 2}
        stroke="rgba(255,255,255,0.05)"
        strokeWidth="1"
      />
      {/* main volatility line */}
      <path
        d={pathD}
        fill="none"
        stroke="currentColor"
        strokeWidth="2.4"
        strokeLinecap="round"
        strokeLinejoin="round"
        style={{
          strokeDasharray: totalLen,
          strokeDashoffset: totalLen,
        }}
        className="vol-wave__line"
      />
      {/* endpoint dot */}
      <circle
        cx={points[points.length - 1].x}
        cy={points[points.length - 1].y}
        r="3.2"
        fill="currentColor"
        className="vol-wave__dot"
      />
    </svg>
  );
}

function generatePoints(variant, width, height) {
  const mid = height / 2;
  const amp = height * 0.42;
  const pts = [];
  const n = variant === 'trading' ? 42 : variant === 'crypto' ? 28 : 60;
  // Deterministic pseudo-noise so SSR / reloads look consistent
  const rand = mulberry32(variant === 'trading' ? 1234 : variant === 'crypto' ? 6789 : 4242);

  for (let i = 0; i <= n; i += 1) {
    const t = i / n;
    const x = t * width;
    let y = mid;

    if (variant === 'trading') {
      // jagged small spikes, slight downward drift
      const noise = (rand() - 0.5) * 2;
      const drift = t * amp * 0.35; // downward = positive Y
      const spike = Math.sin(t * Math.PI * 14) * amp * 0.35;
      y = mid + noise * amp * 0.55 + drift + spike * 0.25;
    } else if (variant === 'stocks') {
      // smooth sinus with slight upward trend
      const drift = -t * amp * 0.55;
      const wave = Math.sin(t * Math.PI * 3.2) * amp * 0.45;
      const wobble = (rand() - 0.5) * amp * 0.12;
      y = mid + wave + drift + wobble;
    } else {
      // crypto: extreme spikes
      const drift = (rand() - 0.5) * amp * 0.4;
      const spike = Math.sin(t * Math.PI * 6) * amp * 0.9;
      const chaos = (rand() - 0.5) * amp * 1.6;
      y = mid + spike + chaos + drift;
    }
    // clamp inside
    y = Math.max(2, Math.min(height - 2, y));
    pts.push({ x, y });
  }
  return pts;
}

function toPath(pts) {
  if (!pts.length) return '';
  return pts.reduce((acc, p, i) => acc + (i === 0 ? `M${p.x.toFixed(2)} ${p.y.toFixed(2)}` : ` L${p.x.toFixed(2)} ${p.y.toFixed(2)}`), '');
}

// Simple deterministic PRNG (Mulberry32)
function mulberry32(seed) {
  let s = seed >>> 0;
  return function () {
    s = (s + 0x6d2b79f5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
